<template>
  <v-container fluid>
    <v-row class="mb-2" align="center">
      <v-col cols="auto" class="text-h6">Konto</v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-btn small text @click="reload">
          <v-icon left small>mdi-refresh</v-icon>
          Aktualisieren
        </v-btn>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined>
          <v-card-subtitle class="pb-0">Depot-Gesamtwert</v-card-subtitle>
          <v-card-title class="text-h5">{{ fmtEur(s('total_value')) }}</v-card-title>
          <v-card-text class="text--secondary">
            {{ s('open_positions') || 0 }} offene Positionen
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined>
          <v-card-subtitle class="pb-0">Offener G/V</v-card-subtitle>
          <v-card-title class="text-h5" :class="gainClass(s('total_win_abs'))">
            {{ fmtSignedEur(s('total_win_abs')) }}
          </v-card-title>
          <v-card-text :class="gainClass(s('total_win_rel'))">
            {{ fmtPct(s('total_win_rel')) }}
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined>
          <v-card-subtitle class="pb-0">Realisierter G/V</v-card-subtitle>
          <v-card-title class="text-h5" :class="gainClass(s('realized_gain_abs'))">
            {{ fmtSignedEur(s('realized_gain_abs')) }}
          </v-card-title>
          <v-card-text class="text--secondary">
            {{ s('closed_sales') || 0 }} Verkäufe · Dividenden {{ fmtEur(s('dividends_total')) }}
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined>
          <v-card-subtitle class="pb-0">Rendite p.a.</v-card-subtitle>
          <v-card-title class="text-h5" :class="gainClass(s('overall_pa'))">
            {{ fmtPct(s('overall_pa')) }}
          </v-card-title>
          <v-card-text class="text--secondary">
            Overall {{ fmtSignedEur(s('overall_gain_abs')) }}
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="goal" dense>
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="text-subtitle-1 d-flex align-center flex-wrap">
            <v-icon left small>mdi-target</v-icon>
            Ziel: {{ goal.name }}
            <v-chip
                :color="goal.on_track ? 'green darken-2' : 'orange darken-3'"
                small
                dark
                class="ml-3"
            >
              <v-icon left small>{{ goal.on_track ? 'mdi-trending-up' : 'mdi-trending-down' }}</v-icon>
              {{ goal.on_track ? 'Vor Plan' : 'Im Rückstand' }} {{ fmtSignedEur(goal.delta) }}
            </v-chip>
            <v-spacer />
            <span class="text--secondary text-body-2">
              bis {{ fmtDate(goal.end_date) }} · noch {{ goal.days_remaining }} Tage
            </span>
          </v-card-title>
          <v-card-subtitle class="pt-0 pb-2 text-body-2 text--secondary">
            Start {{ fmtEur(goal.baseline_value) }}
            <span v-if="goal.start_date"> ({{ fmtDate(goal.start_date) }})</span>
            · Wachstumsziel +{{ (goal.target_pct * 100).toFixed(1) }} % ({{ fmtEur(goal.growth_target) }})
            <span v-if="goal.monthly_contribution > 0">
              · Sparrate {{ fmtEur(goal.monthly_contribution) }}/Monat
              · Σ {{ fmtEur(goal.expected_contributions) }} über {{ goal.contribution_months }} Monate
            </span>
          </v-card-subtitle>
          <v-card-text>
            <div class="d-flex justify-space-between text-body-2 mb-1">
              <span class="text--secondary">Fortschritt</span>
              <span class="font-weight-medium" :class="gainClass(goal.delta)">
                {{ goal.progress_pct != null ? goal.progress_pct.toFixed(1) + ' %' : '—' }}
              </span>
            </div>
            <v-progress-linear
                :value="progressBar"
                :color="goal.on_track ? 'green darken-2' : 'orange darken-3'"
                height="18"
                rounded
            >
              <span class="white--text text-caption">
                {{ fmtEur(goal.current_value) }} / {{ fmtEur(goal.target_value) }}
              </span>
            </v-progress-linear>

            <v-row dense class="mt-3">
              <v-col cols="6" sm="3">
                <div class="text-caption text--secondary">Aktueller Wert</div>
                <div class="text-subtitle-1">{{ fmtEur(goal.current_value) }}</div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="text-caption text--secondary">Soll heute</div>
                <div class="text-subtitle-1">{{ fmtEur(goal.soll_heute) }}</div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="text-caption text--secondary">Noch bis Ziel</div>
                <div class="text-subtitle-1">{{ fmtEur(goal.remaining_to_target) }}</div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="text-caption text--secondary">Wachstum seit Start</div>
                <div class="text-subtitle-1" :class="gainClass(goal.gain_so_far)">
                  {{ fmtSignedEur(goal.gain_so_far) }}
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-card
            outlined
            class="dropzone"
            :class="{ 'dropzone--active': dragOver }"
            @dragenter.prevent="dragOver = true"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="onDrop"
        >
          <v-card-text class="text-center pa-6">
            <v-icon size="40" :color="dragOver ? 'primary' : 'grey'">mdi-file-upload-outline</v-icon>
            <div class="text-subtitle-1 mt-2">
              Visa-Abrechnung (PDF) hierher ziehen
            </div>
            <div class="text--secondary text-body-2">
              oder
              <a href="#" class="ml-1" @click.prevent="$refs.statementFile.click()">Datei auswählen</a>
            </div>
            <input
                ref="statementFile"
                type="file"
                accept="application/pdf,.pdf"
                style="display: none"
                @change="onFileChange"
            />
            <v-alert v-if="uploadStatus === 'uploading'" type="info" dense outlined class="mt-3 mb-0 text-left">
              <v-progress-circular indeterminate size="16" width="2" class="mr-2" />
              Lade {{ uploadFilename }} hoch und importiere …
            </v-alert>
            <v-alert v-if="uploadStatus === 'success'" type="success" dense outlined class="mt-3 mb-0 text-left">
              {{ uploadFilename }} importiert.
            </v-alert>
            <v-alert v-if="uploadStatus === 'error'" type="error" dense outlined class="mt-3 mb-0 text-left">
              {{ uploadError }}
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" lg="6">
        <v-card outlined>
          <v-card-title class="text-subtitle-1">Monats-Übersicht</v-card-title>
          <v-data-table
              :headers="monthHeaders"
              :items="months"
              :items-per-page="-1"
              multi-sort
              :sort-by="['year', 'month']"
              :sort-desc="[true, true]"
              dense
              show-expand
              single-expand
              item-key="label"
              :expanded.sync="expandedMonths"
              no-data-text="Keine Monatsdaten"
          >
            <template v-slot:item.month="{ item }">
              {{ monthName(item.month) }}
            </template>
            <template v-slot:item.income="{ item }">
              <span class="green--text text--darken-2">{{ fmtEur(item.income) }}</span>
            </template>
            <template v-slot:item.expenses="{ item }">
              <span class="red--text text--darken-2">{{ fmtEur(item.expenses) }}</span>
            </template>
            <template v-slot:item.savings_transfers="{ item }">
              {{ fmtEur(item.savings_transfers) }}
            </template>
            <template v-slot:item.net="{ item }">
              <span :class="gainClass(item.net)">{{ fmtSignedEur(item.net) }}</span>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-3 grey lighten-5">
                <div class="text-caption text--secondary mb-2">
                  Einnahmen {{ monthName(item.month) }} {{ item.year }} —
                  {{ (item.income_items || []).length }} Positionen, Σ {{ fmtEur(item.income) }}
                </div>
                <v-simple-table v-if="(item.income_items || []).length" dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Datum</th>
                        <th class="text-left">Absender / Zweck</th>
                        <th class="text-right">Betrag</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(pos, i) in item.income_items" :key="i">
                        <td class="text-no-wrap">{{ fmtDate(pos.booking_date) }}</td>
                        <td>
                          <div>
                            {{ pos.applicant_name || '—' }}
                            <v-chip v-if="pos.source === 'creditcard'" x-small label class="ml-1" color="orange lighten-4">Visa</v-chip>
                          </div>
                          <div class="text--secondary text-caption">{{ pos.purpose || pos.posting_text }}</div>
                        </td>
                        <td class="text-right green--text text--darken-2">{{ fmtSignedEur(pos.amount) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <div v-else class="text-body-2 text--secondary">Keine Einnahmen in diesem Monat.</div>
              </td>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <v-col cols="12" lg="6">
        <v-card outlined>
          <v-card-title class="text-subtitle-1">Letzte Buchungen</v-card-title>
          <v-data-table
              :headers="txnHeaders"
              :items="transactions"
              :items-per-page="-1"
              sort-by="booking_date"
              :sort-desc="true"
              dense
              no-data-text="Keine Buchungen"
          >
            <template v-slot:item.booking_date="{ item }">
              {{ fmtDate(item.booking_date) }}
            </template>
            <template v-slot:item.amount="{ item }">
              <span :class="gainClass(item.amount)">{{ fmtSignedEur(item.amount) }}</span>
            </template>
            <template v-slot:item.applicant_name="{ item }">
              <div>{{ item.applicant_name || '—' }}</div>
              <div class="text--secondary text-caption text-truncate" style="max-width: 280px;">
                {{ item.purpose || item.posting_text }}
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {fmtDate, fmtEur, fmtSignedEur, fmtPct, gainClass} from "./finanzenFormat";

export default {
  name: "KontoView",
  data: () => ({
    monthHeaders: [
      {text: "Jahr", value: "year"},
      {text: "Monat", value: "month"},
      {text: "Einnahmen", value: "income", align: "end"},
      {text: "Ausgaben", value: "expenses", align: "end"},
      {text: "Sparen", value: "savings_transfers", align: "end"},
      {text: "Netto", value: "net", align: "end"},
    ],
    monthLabels: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
    txnHeaders: [
      {text: "Datum", value: "booking_date"},
      {text: "Absender / Zweck", value: "applicant_name"},
      {text: "Betrag", value: "amount", align: "end"},
    ],
    expandedMonths: [],
    dragOver: false,
    uploadStatus: 'idle', // 'idle' | 'uploading' | 'success' | 'error'
    uploadFilename: '',
    uploadError: '',
  }),
  computed: {
    summary() {
      return this.$store.state.smarthaus.finanzen.summary
    },
    goal() {
      return this.$store.state.smarthaus.finanzen.goal
    },
    progressBar() {
      // Balken auf 0–100 begrenzen, auch wenn das Ziel über-/untererfüllt ist.
      if (!this.goal || this.goal.progress_pct == null) return 0
      return Math.min(Math.max(this.goal.progress_pct, 0), 100)
    },
    months() {
      return this.$store.state.smarthaus.finanzen.giroMonths
    },
    transactions() {
      return this.$store.state.smarthaus.finanzen.giroTransactions
    },
  },
  methods: {
    fmtDate, fmtEur, fmtSignedEur, fmtPct, gainClass,
    s(key) {
      return this.summary ? this.summary[key] : null
    },
    monthName(m) {
      return this.monthLabels[(m - 1) % 12] || m
    },
    reload() {
      this.$store.dispatch('update', {"context": '/portfolio/summary'})
      this.$store.dispatch('update', {"context": '/portfolio/goal'})
      this.$store.dispatch('update', {"context": '/giro/monthly'})
      this.$store.dispatch('update', {"context": '/giro/transactions?limit=50'})
    },
    onDrop(e) {
      this.dragOver = false
      const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
      if (f) this.uploadStatement(f)
    },
    onFileChange(e) {
      const f = e.target.files && e.target.files[0]
      if (f) this.uploadStatement(f)
      e.target.value = '' // reset, sonst feuert change beim erneuten Hochladen derselben Datei nicht
    },
    async uploadStatement(file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        this.uploadStatus = 'error'
        this.uploadError = 'Nur PDF-Dateien werden akzeptiert.'
        return
      }
      this.uploadStatus = 'uploading'
      this.uploadFilename = file.name
      this.uploadError = ''
      const fd = new FormData()
      fd.append('file', file)
      try {
        const res = await fetch('/api/bank/upload-statement', {
          method: 'POST',
          body: fd,
        })
        if (!res.ok) {
          const text = await res.text().catch(() => '')
          throw new Error(text || `HTTP ${res.status}`)
        }
        this.uploadStatus = 'success'
        this.reload()
        setTimeout(() => {
          if (this.uploadStatus === 'success') this.uploadStatus = 'idle'
        }, 5000)
      } catch (err) {
        this.uploadStatus = 'error'
        this.uploadError = 'Upload fehlgeschlagen: ' + ((err && err.message) || err)
      }
    },
  },
  mounted() {
    this.reload()
  },
}
</script>

<style scoped>
.dropzone {
  border-style: dashed !important;
  border-width: 2px !important;
  transition: border-color 120ms ease-out, background-color 120ms ease-out;
}
.dropzone--active {
  border-color: var(--v-primary-base, #1976d2) !important;
  background-color: rgba(25, 118, 210, 0.06);
}
</style>
